import { ScoreInput } from './scoring';
import { getStadium } from './stadiums';

const ODDS_API_KEY = process.env.REACT_APP_ODDS_API_KEY;
const ODDS_BASE = 'https://api.the-odds-api.com/v4';
const MLB_BASE = 'https://statsapi.mlb.com/api/v1';
const WEATHER_BASE = 'https://api.open-meteo.com/v1';

// ── HELPERS ──────────────────────────────────────────────────────────────────

function todayEST(): string {
  return new Date().toLocaleDateString('en-CA', {
    timeZone: 'America/New_York',
  });
}

function formatHour(isoString: string): number {
  return new Date(isoString).getHours();
}

// ── MLB TEAM ABBR MAP ─────────────────────────────────────────────────────────
// Maps MLB Stats API team IDs to our stadium abbreviations

const TEAM_ID_MAP: Record<number, string> = {
  108: 'LAA', 109: 'ARI', 110: 'BAL', 111: 'BOS',
  112: 'CHC', 113: 'CIN', 114: 'CLE', 115: 'COL',
  116: 'DET', 117: 'HOU', 118: 'KCR', 119: 'LAD',
  120: 'WSN', 121: 'NYM', 133: 'ATH', 134: 'PIT',
  135: 'SDP', 136: 'SEA', 137: 'SFG', 138: 'STL',
  139: 'TBR', 140: 'TEX', 141: 'TOR', 142: 'MIN',
  143: 'PHI', 144: 'ATL', 145: 'CWS', 146: 'MIA',
  147: 'NYY', 158: 'MIL',
};

// ── ODDS API — GAME TOTALS ────────────────────────────────────────────────────

interface OddsGame {
  id: string;
  home_team: string;
  away_team: string;
  commence_time: string;
  bookmakers: {
    key: string;
    markets: {
      key: string;
      outcomes: { name: string; point: number }[];
    }[];
  }[];
}

async function fetchGameTotals(): Promise<Record<string, number>> {
  try {
    const res = await fetch(
      `${ODDS_BASE}/sports/baseball_mlb/odds/?apiKey=${ODDS_API_KEY}&regions=us&markets=totals&oddsFormat=american`
    );
    if (!res.ok) throw new Error(`Odds API error: ${res.status}`);
    const games: OddsGame[] = await res.json();

    const totals: Record<string, number> = {};

    for (const game of games) {
      for (const book of game.bookmakers) {
        const totalMarket = book.markets.find(m => m.key === 'totals');
        if (totalMarket) {
          const over = totalMarket.outcomes.find(o => o.name === 'Over');
          if (over) {
            // key by "AWAY_HOME" normalized team names
            const key = `${game.away_team}|${game.home_team}`;
            totals[key] = over.point;
            break;
          }
        }
      }
    }

    return totals;
  } catch (err) {
    console.error('fetchGameTotals error:', err);
    return {};
  }
}

// ── OPEN-METEO — WEATHER ─────────────────────────────────────────────────────

interface WeatherResult {
  tempF: number;
  windMph: number;
  windDeg: number;
}

async function fetchWeather(
  lat: number,
  lng: number,
  gameHour: number
): Promise<WeatherResult> {
  try {
    const res = await fetch(
      `${WEATHER_BASE}/forecast?latitude=${lat}&longitude=${lng}` +
      `&hourly=temperature_2m,windspeed_10m,winddirection_10m` +
      `&temperature_unit=fahrenheit&windspeed_unit=mph&timezone=auto&forecast_days=1`
    );
    if (!res.ok) throw new Error(`Weather API error: ${res.status}`);
    const data = await res.json();

    const hourly = data.hourly;
    const idx = Math.min(gameHour, hourly.time.length - 1);

    return {
      tempF:   hourly.temperature_2m[idx]     ?? 70,
      windMph: hourly.windspeed_10m[idx]       ?? 5,
      windDeg: hourly.winddirection_10m[idx]   ?? 180,
    };
  } catch (err) {
    console.error('fetchWeather error:', err);
    return { tempF: 70, windMph: 5, windDeg: 180 };
  }
}

// ── MLB STATS API — SCHEDULE + PITCHERS + STATS ───────────────────────────────

interface MLBGame {
  gamePk: number;
  gameDate: string;
  teams: {
    away: {
      team: { id: number; name: string };
      probablePitcher?: { id: number; fullName: string };
    };
    home: {
      team: { id: number; name: string };
      probablePitcher?: { id: number; fullName: string };
    };
  };
  venue: { name: string };
}

async function fetchPitcherHR9(pitcherId: number): Promise<number> {
  try {
    const res = await fetch(
      `${MLB_BASE}/people/${pitcherId}/stats?stats=season&group=pitching&season=2026`
    );
    if (!res.ok) return 1.0;
    const data = await res.json();
    const stats = data.stats?.[0]?.splits?.[0]?.stat;
    if (!stats) return 1.0;

    const hr = stats.homeRunsPer9 ?? stats.hrsPer9 ?? null;
    if (hr !== null) return parseFloat(hr);

    // Calculate manually if not provided
    const innings = parseFloat(stats.inningsPitched ?? '1');
    const homeRuns = stats.homeRuns ?? 0;
    return innings > 0 ? (homeRuns / innings) * 9 : 1.0;
  } catch {
    return 1.0;
  }
}

async function fetchTeamSLG(teamId: number): Promise<number> {
  try {
    const res = await fetch(
      `${MLB_BASE}/teams/${teamId}/stats?stats=season&group=hitting&season=2026`
    );
    if (!res.ok) return 0.400;
    const data = await res.json();
    const stats = data.stats?.[0]?.splits?.[0]?.stat;
    const slg = parseFloat(stats?.slg ?? '0.400');
    return isNaN(slg) ? 0.400 : slg;
  } catch {
    return 0.400;
  }
}

async function fetchSchedule(): Promise<MLBGame[]> {
  try {
    const date = todayEST();
    const res = await fetch(
      `${MLB_BASE}/schedule?sportId=1&date=${date}&hydrate=probablePitcher,team,venue`
    );
    if (!res.ok) throw new Error(`MLB API error: ${res.status}`);
    const data = await res.json();
    const games: MLBGame[] = [];
    for (const date of data.dates ?? []) {
      for (const game of date.games ?? []) {
        games.push(game);
      }
    }
    return games;
  } catch (err) {
    console.error('fetchSchedule error:', err);
    return [];
  }
}

// ── MAIN EXPORT — FETCH ALL DATA ──────────────────────────────────────────────

export async function fetchTodaysGames(): Promise<ScoreInput[]> {
  // 1. Get today's schedule
  const schedule = await fetchSchedule();
  if (schedule.length === 0) return [];

  // 2. Get game totals from Odds API
  const totals = await fetchGameTotals();

  // 3. Build ScoreInput for each game in parallel
  const inputs = await Promise.all(
    schedule.map(async (game, idx): Promise<ScoreInput | null> => {
      const awayTeamId = game.teams.away.team.id;
      const homeTeamId = game.teams.home.team.id;
      const awayAbbr = TEAM_ID_MAP[awayTeamId] ?? 'UNK';
      const homeAbbr = TEAM_ID_MAP[homeTeamId] ?? 'UNK';

      const stadium = getStadium(homeAbbr);
      if (!stadium) return null;

      // Pitchers
      const awayPitcherId = game.teams.away.probablePitcher?.id;
      const homePitcherId = game.teams.home.probablePitcher?.id;
      const awayPitcherName = game.teams.away.probablePitcher?.fullName ?? 'TBD';
      const homePitcherName = game.teams.home.probablePitcher?.fullName ?? 'TBD';

      // Fetch pitcher HR/9, team SLG, weather in parallel
      const [awayHR9, homeHR9, awaySLG, homeSLG, weather] = await Promise.all([
        awayPitcherId ? fetchPitcherHR9(awayPitcherId) : Promise.resolve(1.0),
        homePitcherId ? fetchPitcherHR9(homePitcherId) : Promise.resolve(1.0),
        fetchTeamSLG(awayTeamId),
        fetchTeamSLG(homeTeamId),
        stadium.dome
          ? Promise.resolve({ tempF: 72, windMph: 0, windDeg: 0 })
          : fetchWeather(
              stadium.lat,
              stadium.lng,
              formatHour(game.gameDate)
            ),
      ]);

      // Game total — try to match by team name
      const totalKey = Object.keys(totals).find(k => {
        const [away, home] = k.split('|');
        return (
          away.toLowerCase().includes(game.teams.away.team.name.toLowerCase().split(' ').pop()?.toLowerCase() ?? '') ||
          home.toLowerCase().includes(game.teams.home.team.name.toLowerCase().split(' ').pop()?.toLowerCase() ?? '')
        );
      });
      const total = totalKey ? totals[totalKey] : 8.5;

      // Game time for display
      const gameTime = new Date(game.gameDate).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/New_York',
      });

      return {
        id: idx + 1,
        away: game.teams.away.team.name,
        home: game.teams.home.team.name,
        awayAbbr,
        homeAbbr,
        venue: game.venue.name,
        time: gameTime,
        awayPitcher: awayPitcherName,
        homePitcher: homePitcherName,
        awayHR9,
        homeHR9,
        awaySLG,
        homeSLG,
        total,
        tempF: weather.tempF,
        windMph: weather.windMph,
        windDeg: weather.windDeg,
      };
    })
  );

  return inputs.filter((i): i is ScoreInput => i !== null);
}
